/**
 * configurations for Esri map (configurations that will change Esri map behavior/functionalities)
 *
 * for paddings/margins, shared variables, put them in `constants`
 */

import { EsriBasemap, FontProperties } from './types';
import basemapToggleSatellite from './images/map-modes/basemap-toggle-satellite.jpg';
import basemapToggleDark from './images/map-modes/basemap-toggle-dark.jpg';
import basemapToggleLight from './images/map-modes/basemap-toggle-light.jpg';
import { GenericObject } from '../common/types';

/** Base maps
 *
 *  These maps load from the ArcGis basemap style service.
 *
 *  Find map options here: https://developers.arcgis.com/rest/basemap-styles/ *
 */
export enum EsriBasemapsIds {
    DARK = 'arcgis/dark-gray',
    LIGHT = 'arcgis/light-gray',
    DARK_HUMAN = 'arcgis/human-geography-dark',
    LIGHT_HUMAN = 'arcgis/human-geography',
    SATELLITE = 'arcgis/imagery',
}

export const ESRI_BASEMAPS: GenericObject<EsriBasemap> = {
    DARK: {
        mode: 'DARK_MODE',
        id: EsriBasemapsIds.DARK,
        image: basemapToggleDark,
        isPortalItem: false,
    },
    LIGHT: {
        mode: 'LIGHT_MODE',
        id: EsriBasemapsIds.LIGHT,
        image: basemapToggleLight,
        isPortalItem: false,
    },
    DARK_HUMAN: {
        mode: 'DARK_HUMAN_MODE',
        id: EsriBasemapsIds.DARK_HUMAN,
        image: basemapToggleDark,
        isPortalItem: false,
    },
    LIGHT_HUMAN: {
        mode: 'LIGHT_HUMAN_MODE',
        id: EsriBasemapsIds.LIGHT_HUMAN,
        image: basemapToggleLight,
        isPortalItem: false,
    },
    SATELLITE: {
        mode: 'SATELLITE_MODE',
        id: EsriBasemapsIds.SATELLITE,
        image: basemapToggleSatellite,
        isPortalItem: false,
    },
};



/** Alternate base maps
 *
 *  These maps are used in case other maps failed to load. Esri hosts different types of basemaps in different subdomains,
 *   when the regular one failed (load via basemapstyles-api service), the basemaps from portal items may still work.
 *  See: https://developers.arcgis.com/javascript/latest/api-reference/esri-Basemap.html
 *
 *  Map source: https://esri.maps.arcgis.com/home/gallery.html?sortField=relevance&sortOrder=desc&contentstatus=authoritative&focus=maps-webmaps
 *  In the link above, find other maps.  Ensure the "Authoratative flag" is set for the most accurate maps.
 *
 */
export enum EsriAlternativeBasemapsIds {
    DARK = '358ec1e175ea41c3bf5c68f0da11ae2b', // Dark gray canvas https://www.arcgis.com/apps/mapviewer/index.html?webmap=358ec1e175ea41c3bf5c68f0da11ae2b
    LIGHT = '979c6cc89af9449cbeb5342a439c6a76', // Light gray canvas https://www.arcgis.com/apps/mapviewer/index.html?webmap=979c6cc89af9449cbeb5342a439c6a76
    //DARK = '7e2b9be8a9c94e45b7f87857d8d168d6', Streets (Night) // Streets (Night) https://www.arcgis.com/apps/mapviewer/index.html?webmap=7e2b9be8a9c94e45b7f87857d8d168d6
    //LIGHT = '55ebf90799fa4a3fa57562700a68c405', // Streets https://www.arcgis.com/apps/mapviewer/index.html?webmap=55ebf90799fa4a3fa57562700a68c405
    DARK_HUMAN = '4f2e99ba65e34bb8af49733d9778fb8e', //Human Geography (Night) https://www.arcgis.com/apps/mapviewer/index.html?webmap=4f2e99ba65e34bb8af49733d9778fb8e
    LIGHT_HUMAN = '3582b744bba84668b52a16b0b6942544', // Human Geography https://www.arcgis.com/apps/mapviewer/index.html?webmap=3582b744bba84668b52a16b0b6942544
    SATELLITE = '07e6364e724443e8b1cdd77b4482936c', // https://www.arcgis.com/apps/mapviewer/index.html?webmap=07e6364e724443e8b1cdd77b4482936c
}
export const ESRI_ALTERNATIVE_BASEMAPS: GenericObject<EsriBasemap> = {
    DARK: {
        mode: 'DARK_MODE',
        id: EsriAlternativeBasemapsIds.DARK,
        image: basemapToggleDark,
        isPortalItem: true,
    },
    LIGHT: {
        mode: 'LIGHT_MODE',
        id: EsriAlternativeBasemapsIds.LIGHT,
        image: basemapToggleLight,
        isPortalItem: true,
    },
    DARK_HUMAN: {
        mode: 'DARK_HUMAN_MODE',
        id: EsriAlternativeBasemapsIds.DARK_HUMAN,
        image: basemapToggleDark,
        isPortalItem: true,
    },
    LIGHT_HUMAN: {
        mode: 'LIGHT_HUMAN_MODE',
        id: EsriAlternativeBasemapsIds.LIGHT_HUMAN,
        image: basemapToggleLight,
        isPortalItem: true,
    },
    SATELLITE: {
        mode: 'SATELLITE_MODE',
        id: EsriAlternativeBasemapsIds.SATELLITE,
        image: basemapToggleSatellite,
        isPortalItem: true,
    },
};

export const ESRI_MAP_DEFAULT_ROTATION_ENABLED = false;

// https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#zoom
// global view
export const ESRI_MAP_MIN_ZOOM_LEVEL = 0;
// reset to department position and zoom level (zoom to view deparmtent). User can save a zoom level in workspace as default zoom level, otherwise fallback to this hard-coded value.
export const ESRI_MAP_DEFAULT_ZOOM_LEVEL = 14;
// zoom level for viewing event/unit, more detailed zoom level to view entity. Not customizable.
export const ESRI_MAP_VIEW_ENTITY_ZOOM_LEVEL = 18;
// most detailed view
export const ESRI_MAP_MAX_ZOOM_LEVEL = 32;

// Defines the radius in points (or pixels if specified) of the area in which multiple points will be grouped and visualized as a single cluster. Adjusting clusterRadius generally should be considered if the clusterMaxSize is modified.
// if you prefer cluster graphics to not overlap, then the clusterRadius should be substantially larger than the clusterMaxSize.
// the value is set by `cad.map_cluster_radius.pixels`, FE will set to this default if app setting is missing
export const ESRI_MAP_UNIT_LAYER_CLUSTER_RADIUS = 96;
// set max and min to same value so all clusters have same size.
export const ESRI_MAP_UNIT_LAYER_CLUSTER_MAX_SIZE = 40;
export const ESRI_MAP_UNIT_LAYER_CLUSTER_MIN_SIZE = 40;

export const ESRI_EXTENT_RECOVERY_RETRY_LIMIT = 10;

// https://developers.arcgis.com/documentation/spatial-references/
export const ESRI_SPATIAL_REFERENCE = {
    // WGS84
    wkid: 4326,
};

export const ESRI_DEFAULT_ICON_LABEL_FONT: FontProperties = {
    family: 'Montserrat',
    style: 'normal',
    weight: 'bold',
    size: '12px',
};

// throttle threshold in ms for batched hit test to find which graphic should be highlighted when hover. If there are many graphic in a small area,
// move mouse quickly will trigger many hover changes.
export const ESRI_BATCHED_HIT_TEST_THROTTLE_THRESHOLD = 100;

export const ESRI_HIGHLIGHT_FILL_OPACITY = 0.16;

// use-measurements unit
export const ESRI_MEASUREMENTS_LINEAR_UNIT = 'imperial';
export const ESRI_MEASUREMENTS_AREA_UNIT = 'imperial';

// if MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY exists in sessionStorage, esri will load alternative basemaps.
export const MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY =
    'alternative_basemap_activation_datetime';
